// 카메라 배경 흐림 전담. 원본 카메라 트랙을 받아 (인물 원본 + 흐린 배경) 트랙을 돌려준다. av.js는 이 트랙만 안다.
// MediaPipe ImageSegmenter(selfie segmenter)는 처음 쓸 때만 jsdelivr에서 불러온다.
export const Blur = (() => {
  const BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.1.0';
  const MODEL = 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite';
  const PX = 14, FPS = 30, LOAD_MS = 30000;
  const api = { stats: { fps: 0, ms: 0, delegate: '', path: '' } };   // 시험·진단용: 처리 속도(fps), 프레임당 ms, GPU/CPU, 파이프라인 종류
  let segP = null, lastTs = 0;

  function load() {
    return segP = segP || Promise.race([
      (async () => {
        const { FilesetResolver, ImageSegmenter } = await import(BASE + '/vision_bundle.mjs');
        const fs = await FilesetResolver.forVisionTasks(BASE + '/wasm');
        const mk = delegate => ImageSegmenter.createFromOptions(fs, { baseOptions: { modelAssetPath: MODEL, delegate }, runningMode: 'VIDEO', outputConfidenceMasks: true, outputCategoryMask: false });
        try { const s = await mk('GPU'); api.stats.delegate = 'GPU'; return s; } catch (e) { const s = await mk('CPU'); api.stats.delegate = 'CPU'; return s; }
      })(),
      new Promise((_, no) => setTimeout(() => no(new Error('모델 로드 시간 초과')), LOAD_MS)),
    ]).catch(e => { segP = null; throw e; });
  }

  // 프레임 하나를 받아 합성 결과를 out 캔버스에 그린다
  function composer() {
    const cv = () => document.createElement('canvas'), src = cv(), out = cv(), fg = cv(), mask = cv(), sm = cv();
    const sx = src.getContext('2d'), ox = out.getContext('2d'), fx = fg.getContext('2d'), mx = mask.getContext('2d'), smx = sm.getContext('2d');
    const hasFilter = 'filter' in (ox as any);   // Safari는 canvas filter가 없어 축소→확대로 흐린다
    let img = null;
    return {
      out,
      draw(seg, frame, w, h) {
        if (src.width !== w || src.height !== h) { src.width = out.width = fg.width = w; src.height = out.height = fg.height = h; sm.width = Math.max(1, w >> 3); sm.height = Math.max(1, h >> 3); }
        sx.drawImage(frame, 0, 0, w, h);
        const ts = Math.max(lastTs + 1, Math.round(performance.now())); lastTs = ts;
        const r = seg.segmentForVideo(src, ts), m = r.confidenceMasks[0];
        if (!img || img.width !== m.width || img.height !== m.height) { mask.width = m.width; mask.height = m.height; img = mx.createImageData(m.width, m.height); }
        const f = m.getAsFloat32Array(), d = img.data;
        for (let i = 0; i < f.length; i++) d[i * 4 + 3] = f[i] * 255;
        r.close(); mx.putImageData(img, 0, 0);
        if (hasFilter) { ox.filter = 'blur(' + PX + 'px)'; ox.drawImage(src, -PX, -PX, w + 2 * PX, h + 2 * PX); ox.filter = 'none'; }
        else { smx.drawImage(src, 0, 0, sm.width, sm.height); ox.drawImage(sm, 0, 0, w, h); }
        fx.globalCompositeOperation = 'copy'; fx.drawImage(src, 0, 0);
        fx.globalCompositeOperation = 'destination-in'; fx.drawImage(mask, 0, 0, w, h);   // 인물 밖은 투명
        ox.drawImage(fg, 0, 0);
      },
    };
  }

  // raw: 카메라 MediaStreamTrack. 모델을 못 불러오면 throw. 돌려주는 stop()은 파이프라인만 끄고 raw는 건드리지 않는다.
  async function start(raw) {
    const seg = await load(), c = composer(), s = api.stats;
    let stopped = false, t0 = performance.now(), n = 0, busy = 0, warned = false;
    const frame = (src, w, h) => {
      const t = performance.now();
      try { c.draw(seg, src, w, h); } catch (e) { if (!warned) { warned = true; console.warn('[blur]', e); } return false; }   // 실패한 프레임은 버린다 (원본으로 대체하지 않는다)
      const now = performance.now(); busy += now - t; n++;
      if (now - t0 >= 1000) { s.fps = +(n * 1000 / (now - t0)).toFixed(1); s.ms = +(busy / n).toFixed(1); t0 = now; n = busy = 0; }
      return true;
    };
    if (window.MediaStreamTrackProcessor && window.MediaStreamTrackGenerator) {   // 탭이 가려져도 멈추지 않는 경로 (Chrome·Edge)
      s.path = 'processor';
      const gen = new MediaStreamTrackGenerator({ kind: 'video' }), proc = new MediaStreamTrackProcessor({ track: raw }), ac = new AbortController();
      const ts = new TransformStream({ transform(vf, ctl) { try { if (frame(vf, vf.displayWidth, vf.displayHeight)) ctl.enqueue(new VideoFrame(c.out, { timestamp: vf.timestamp })); } finally { vf.close(); } } });
      proc.readable.pipeThrough(ts, { signal: ac.signal }).pipeTo(gen.writable, { signal: ac.signal }).catch(() => {});
      return { track: gen, stop() { ac.abort(); gen.stop(); } };
    }
    s.path = 'video-canvas';   // 대체 경로: 탭이 가려지면 멈춘다 (Safari·Firefox)
    const v = Object.assign(document.createElement('video'), { muted: true, playsInline: true, srcObject: new MediaStream([raw]) });
    await v.play();
    const loop = () => {
      if (stopped) return;
      if (v.videoWidth) frame(v, v.videoWidth, v.videoHeight);
      if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(loop); else setTimeout(loop, 1000 / FPS);
    };
    loop();
    const track = c.out.captureStream(FPS).getVideoTracks()[0];
    return { track, stop() { stopped = true; track.stop(); v.pause(); v.srcObject = null; } };
  }

  // 시험용: 실제 카메라 없이 사진 한 장을 640x360 canvas에 그려 30fps 카메라처럼 흘려 보낸다 (사진은 CORS 허용 주소여야 한다)
  async function testPhotoStream(url) {
    const im = await new Promise<any>((ok, no) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => ok(i); i.onerror = no; i.src = url; });
    const cv = Object.assign(document.createElement('canvas'), { width: 640, height: 360 }), x = cv.getContext('2d');
    const k = Math.max(640 / im.width, 360 / im.height), w = im.width * k, h = im.height * k;
    let t = 0; const draw = () => { t++; x.drawImage(im, (640 - w) / 2 + Math.sin(t / 10), 0, w, h); }; draw(); setInterval(draw, 33);
    return cv.captureStream(FPS);
  }

  return Object.assign(api, { start, testPhotoStream });
})();
